import { c as _c } from "react/compiler-runtime";
import { add, differenceInSeconds, isSameYear, sub } from 'date-fns';
import { Form, Formik } from 'formik';
import { useContext, useEffect, useState } from 'react';
import { IgnoredEventCause } from '../../../../../../../../typings/IgnoredEvent.interface';
import { EventListFiltersProps } from '../../../../../typings/EventList.interface';
import { RequestListFiltersProps } from '../../../../../typings/RequestList.interface';
import { chart_resolutions, getBestResolution, getTime, Resolution, startOfTime, timebar_resolutions } from '../../../../../utils/chart';
import { formatDate, getBrowserTimezone, getTimezoneAbbreviation, relative_dates, RelativeDate } from '../../../../../utils/date';
import formatMetricsQuery, { MetricModel } from '../../../../../utils/formatMetricsQuery';
import { hasSeconds, roundUpHour } from '../../../../../utils/time';
import Button from '../../../../common/base/Button';
import { StyledCardSection } from '../../../../common/base/Card';
import Text from '../../../../common/base/Text';
import Dropdown from '../../../../common/Dropdown';
import DatePickerInput, { formatDateFilterValues } from '../../../../common/Form/Fields/DatePickerInput';
import { BROWSER_TIMEZONE_OPTION, resolveTimezoneOption } from '../../../../common/Form/Fields/TimezoneSelectInput';
import { Div } from '../../../../common/helpers/StyledUtils';
import HistogramRangePicker from '../../../../common/Histogram/HistogramRangePicker';
import { useDatePreferences, useUpdateTimezonePreference } from '../../../../hooks/useDatePreferences';
import { DashboardContext } from '../../DashboardContext';
import HistogramChart from './Chart';
import { OutpostEventFilters } from '../../../OutpostScene/Events/EventsView';
import { OutpostAttemptFilters } from '../../../OutpostScene/Attempts/utils';
type Filters = EventListFiltersProps | RequestListFiltersProps | OutpostEventFilters | OutpostAttemptFilters
// Typings for IgnoredEvents cube Model
| {
  date?: {
    relative?: RelativeDate;
    max?: string;
    min?: string;
  };
  cause: IgnoredEventCause[];
};
export const getTimerangeLabel = (date: {
  relative?: RelativeDate;
  min?: string;
  max?: string;
}, timezone = getBrowserTimezone()): React.ReactNode => {
  if (date?.relative) {
    return relative_dates[date.relative].label;
  }
  if (date?.max || date?.min) {
    const start_date = new Date(date.min!);
    const end_date = date.max && new Date(date.max);
    const date_format = end_date && !isSameYear(start_date, end_date) ? 'MMM d yy' : 'MMM d';
    const format_start_string = date.min && hasSeconds(date.min) ? `${date_format}, h:mm:ss a` : `${date_format}, h:mm a`;
    const format_end_string = date.max && hasSeconds(date.max) ? `${date_format}, h:mm:ss a` : `${date_format}, h:mm a`;
    return <Div as="span" flex={{
      align: 'center',
      gap: 1
    }}>
        {date?.min ? formatDate(start_date, format_start_string, timezone) : `${formatDate(start_date, format_start_string, timezone)} (Retention window)`}{' '}
        {'->'} {date?.max && end_date ? formatDate(end_date, format_end_string, timezone) : 'Now'}
      </Div>;
  }
  return `Last 24 hours`;
};
export const zoom = (direction: 'in' | 'out', date: Date, resolution: Resolution) => {
  if (direction === 'in') {
    return sub(date, {
      [`${resolution.granularity}s`]: getTime[resolution.granularity](date) + resolution.unit
    });
  }
  if (direction === 'out') {
    return add(date, {
      [`${resolution.granularity}s`]: getTime[resolution.granularity](date) + resolution.unit
    });
  }
  return date;
};
export const getHistogramConfigs = (diff: number, start_date: Date, end_date: Date) => {
  let chart_resolution = getBestResolution(diff, 60, chart_resolutions).best;
  let axis_resolution = getBestResolution(diff, 10, timebar_resolutions).best;

  // if the granularity is hour, always round up so the time is greater then the time inputted
  if (chart_resolution.granularity === 'hour') {
    start_date = roundUpHour(start_date);
    end_date = roundUpHour(end_date);
    const diff = differenceInSeconds(end_date, start_date);
    chart_resolution = getBestResolution(diff, 60, chart_resolutions).best;
    axis_resolution = getBestResolution(diff, 10, timebar_resolutions).best;
  }
  if (chart_resolution.granularity !== 'second') {
    end_date = add(startOfTime[chart_resolution.granularity](end_date), {
      [`${chart_resolution.granularity}s`]: getTime[chart_resolution.granularity](end_date) * chart_resolution.unit / chart_resolution.unit
    });
    start_date = sub(end_date, {
      [`${chart_resolution.granularity}s`]: chart_resolution.steps * chart_resolution.unit
    });
  }
  return {
    start_date,
    end_date,
    chart_resolution,
    axis_resolution
  };
};
export const DateFilter: React.FC<{
  filters: Filters;
  setDate: (date: {
    relative?: RelativeDate;
    min?: string;
    max?: string;
  } | undefined) => void;
  start_date: Date;
  end_date: Date;
}> = t0 => {
  const $ = _c(46);
  const {
    filters,
    setDate,
    start_date,
    end_date
  } = t0;
  const {
    browser_timezone,
    timezone,
    uses_browser_timezone
  } = useDatePreferences();
  const updateTimezonePreference = useUpdateTimezonePreference();
  const {
    subscription,
    team
  } = useContext(DashboardContext);
  const retention_days = team?.type === "outpost" ? subscription.outpost_config.retention_days : subscription.retention_days;
  let t1;
  if ($[0] !== filters.date || $[1] !== timezone) {
    t1 = filters.date ? getTimerangeLabel(filters.date, timezone) : "";
    $[0] = filters.date;
    $[1] = timezone;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const date_label = t1;
  let t2;
  if ($[3] !== filters.date?.min || $[4] !== start_date) {
    t2 = filters.date?.min || start_date.toISOString();
    $[3] = filters.date?.min;
    $[4] = start_date;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  let t3;
  if ($[6] !== end_date || $[7] !== filters.date?.max) {
    t3 = filters.date?.max || end_date.toISOString();
    $[6] = end_date;
    $[7] = filters.date?.max;
    $[8] = t3;
  } else {
    t3 = $[8];
  }
  let t4;
  if ($[9] !== filters.date || $[10] !== t2 || $[11] !== t3) {
    t4 = {
      ...filters.date,
      min: t2,
      max: t3
    };
    $[9] = filters.date;
    $[10] = t2;
    $[11] = t3;
    $[12] = t4;
  } else {
    t4 = $[12];
  }
  const t5 = t4 as {
    relative?: RelativeDate;
    min?: string;
    max?: string;
  };
  let t6;
  if ($[13] !== filters.date || $[14] !== start_date || $[15] !== timezone) {
    t6 = filters.date?.min ? formatDate(filters.date.min, "HH:mm:ss", timezone) : formatDate(start_date, "HH:mm:ss", timezone);
    $[13] = filters.date;
    $[14] = start_date;
    $[15] = timezone;
    $[16] = t6;
  } else {
    t6 = $[16];
  }
  let t7;
  if ($[17] !== end_date || $[18] !== filters.date || $[19] !== timezone) {
    t7 = filters.date?.max ? formatDate(filters.date.max, "HH:mm:ss", timezone) : formatDate(end_date, "HH:mm:ss", timezone);
    $[17] = end_date;
    $[18] = filters.date;
    $[19] = timezone;
    $[20] = t7;
  } else {
    t7 = $[20];
  }
  const t8 = uses_browser_timezone ? BROWSER_TIMEZONE_OPTION : timezone;
  let t9;
  if ($[21] !== t5 || $[22] !== t6 || $[23] !== t7 || $[24] !== t8) {
    t9 = {
      date: t5,
      start_time: t6,
      end_time: t7,
      timezone: t8
    };
    $[21] = t5;
    $[22] = t6;
    $[23] = t7;
    $[24] = t8;
    $[25] = t9;
  } else {
    t9 = $[25];
  }
  const form_initial_values = t9;
  let t10;
  let t11;
  if ($[26] === Symbol.for("react.memo_cache_sentinel")) {
    t10 = {
      align: "baseline",
      gap: 1
    };
    t11 = {
      r: -1
    };
    $[26] = t10;
    $[27] = t11;
  } else {
    t10 = $[26];
    t11 = $[27];
  }
  let t12;
  if ($[28] !== date_label) {
    t12 = <span>{date_label}</span>;
    $[28] = date_label;
    $[29] = t12;
  } else {
    t12 = $[29];
  }
  let t13;
  if ($[30] !== timezone) {
    t13 = getTimezoneAbbreviation(timezone);
    $[30] = timezone;
    $[31] = t13;
  } else {
    t13 = $[31];
  }
  let t14;
  if ($[32] !== t13) {
    t14 = <Text as="span" muted={true} semi={true} size="xs">{t13}</Text>;
    $[32] = t13;
    $[33] = t14;
  } else {
    t14 = $[33];
  }
  let t15;
  if ($[34] !== t12 || $[35] !== t14) {
    t15 = <Div flex={t10} m={t11}>{t12}{t14}</Div>;
    $[34] = t12;
    $[35] = t14;
    $[36] = t15;
  } else {
    t15 = $[36];
  }
  let t16;
  if ($[37] !== browser_timezone || $[38] !== form_initial_values || $[39] !== retention_days || $[40] !== setDate || $[41] !== updateTimezonePreference) {
    t16 = toggle => <Formik initialValues={form_initial_values} onSubmit={async v => {
      const selected_timezone = resolveTimezoneOption(v.timezone, browser_timezone);
      await updateTimezonePreference(v.timezone === BROWSER_TIMEZONE_OPTION ? null : v.timezone);
      const {
        start_date: start_date_0,
        end_date: end_date_0
      } = formatDateFilterValues({
        start_date: v.date.min,
        start_time: v.start_time,
        end_date: v.date.max,
        end_time: v.end_time
      }, selected_timezone);
      setDate({
        ...v.date,
        min: start_date_0,
        max: end_date_0
      });
      toggle(false);
    }}>{props => <Form><StyledCardSection><DatePickerInput display_reset={false} time_format="hh:mm:ss" retention_days={retention_days} /><Div flex={{
            justify: "flex-end",
            gap: 3
          }} p={{
            x: 4,
            b: 4
          }}><Button outline={true} onClick={() => toggle(false)}>Cancel</Button><Button primary={true} submit={true}>Apply</Button></Div></StyledCardSection></Form>}</Formik>;
    $[37] = browser_timezone;
    $[38] = form_initial_values;
    $[39] = retention_days;
    $[40] = setDate;
    $[41] = updateTimezonePreference;
    $[42] = t16;
  } else {
    t16 = $[42];
  }
  let t17;
  if ($[43] !== t15 || $[44] !== t16) {
    t17 = <Dropdown outline={true} expand_icon="chevron_down" icon="date" label={t15}>{t16}</Dropdown>;
    $[43] = t15;
    $[44] = t16;
    $[45] = t17;
  } else {
    t17 = $[45];
  }
  return t17;
};
const Histogram: React.FC<{
  model: MetricModel;
  dimention: string;
  filters: Filters;
  start_date: Date;
  end_date: Date;
  setDate: (date: {
    relative?: RelativeDate;
    min?: string;
    max?: string;
  } | undefined) => void;
  refresh_key?: string;
  allow_reset?: boolean;
  show_date_filter?: boolean;
  onRefresh?: () => void;
}> = ({
  model,
  dimention,
  filters,
  start_date,
  end_date,
  setDate,
  refresh_key,
  allow_reset,
  show_date_filter,
  onRefresh
}) => {
  const [timerange_history, setTimerangeHistory] = useState([{
    min: start_date.toISOString(),
    max: end_date.toISOString()
  }]);
  const diff = differenceInSeconds(end_date, start_date);
  const {
    start_date: new_start_date,
    end_date: new_end_date,
    chart_resolution,
    axis_resolution
  } = getHistogramConfigs(diff, start_date, end_date);
  start_date = new_start_date;
  end_date = new_end_date;
  useEffect(() => {
    if (filters.date?.relative || !filters.date?.min || !filters.date?.max) {
      setTimerangeHistory([{
        min: start_date.toISOString(),
        max: end_date.toISOString()
      }]);
    }
  }, [filters]);
  const setDateAndTimerange = (date: {
    relative?: RelativeDate;
    min: string;
    max: string;
  }) => {
    setTimerangeHistory([{
      min: date.min,
      max: date.max
    }, ...timerange_history]);
    setDate(date);
  };
  const has_request_value_filters = 'request' in filters && filters.request && Object.values(filters.request).some(v => v !== undefined);
  const has_search_filter = 'search_term' in filters && !!filters.search_term;
  const histogram_unavailable = formatMetricsQuery(model, {
    filters: {
      ...filters,
      date: {
        min: start_date.toISOString(),
        max: end_date.toISOString()
      }
    },
    granularity: chart_resolution.granularity,
    dimensions: [dimention]
  }) === null;
  const show_range_controls = !has_request_value_filters && !has_search_filter && !histogram_unavailable;
  return <>
      {(show_date_filter || onRefresh) && <Div m={{
      b: 3
    }} flex={{
      align: 'center',
      justify: 'space-between'
    }}>
          {show_date_filter && <DateFilter filters={filters} setDate={setDate} start_date={start_date} end_date={end_date} />}
          {onRefresh && <Button icon="reload" ghost onClick={() => onRefresh()}>
              Refresh
            </Button>}
        </Div>}
      <Formik key={start_date.toISOString() + end_date.toISOString()} initialValues={{
      timebar_start: 0,
      timebar_end: chart_resolution.steps - 1
    }} onSubmit={values => {
      setDateAndTimerange({
        relative: undefined,
        min: add(start_date, {
          seconds: values.timebar_start * chart_resolution.unit * chart_resolution.factor_to_seconds
        }).toISOString(),
        max: add(start_date, {
          seconds: (values.timebar_end + 1) * chart_resolution.unit * chart_resolution.factor_to_seconds
        }).toISOString()
      });
    }}>
        <Form>
          <HistogramRangePicker chart_resolution={chart_resolution} axis_resolution={axis_resolution} start_date={start_date} show_controls={show_range_controls}>
            <HistogramChart model={model} dimention={dimention} filters={filters as EventListFiltersProps} start_date={start_date} end_date={end_date} resolution={chart_resolution} refresh_key={refresh_key} />
          </HistogramRangePicker>
        </Form>
      </Formik>
    </>;
};
export default Histogram;